import { useIsMounted } from '@toss/react';
import { type SupportedLocale } from '@zep-us/i18n';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  bn,
  de,
  enUS,
  es,
  fr,
  hi,
  id,
  it,
  ja,
  km,
  ko,
  ms,
  nl,
  pl,
  pt,
  ptBR,
  ru,
  ta,
  te,
  th,
  tr,
  uz,
  vi,
  zhCN,
  zhTW,
} from 'date-fns/locale';

import { useLocale } from './useLocale';

function getLocaleModule(locale: SupportedLocale) {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (locale) {
    case 'ko':
      return ko;
    case 'ja':
      return ja;
    case 'id':
      return id;
    case 'th':
      return th;
    case 'fil':
      return enUS; // date-fns doesn't have Filipino locale, fallback to English
    case 'vi':
      return vi;
    case 'ms':
      return ms;
    case 'uz':
      return uz;
    case 'fr':
      return fr;
    case 'km':
      return km;
    case 'ru':
      return ru;
    case 'zh-CN':
      return zhCN;
    case 'zh-TW':
      return zhTW;
    case 'es':
      return es;
    case 'pt':
      return pt;
    case 'pt-BR':
      return ptBR;
    case 'de':
      return de;
    case 'it':
      return it;
    case 'tr':
      return tr;
    case 'nl':
      return nl;
    case 'pl':
      return pl;
    case 'hi-IN':
      return hi;
    case 'bn-IN':
      return bn;
    case 'te-IN':
      return te;
    case 'ta-IN':
      return ta;
    case 'en-SG':
    case 'pa-IN': // date-fns doesn't have Punjabi locale
    case 'ml-IN': // date-fns doesn't have Malayalam locale
    case 'en':
    default:
      return enUS;
  }
}

/**
 * 현재 시간으로부터 지난 시간을 표시하는 훅
 * 1일전, 1달전, 1년전..
 * */
export function useDistanceToNow() {
  const { locale } = useLocale();
  const isMounted = useIsMounted();

  return (date: string) => {
    if (!isMounted) return '';
    // Strict: "약/거의" 근사어 없이 단일 단위로 정확히 표기("약 30분 전"→"30분 전").
    return formatDistanceToNowStrict(date, {
      locale: getLocaleModule(locale),
      addSuffix: true,
    });
  };
}
